import React, { useEffect } from 'react';
import { Table, Button, Tag, Tooltip, Space, Card, Input, Grid, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchShops } from '../../store/shopsSlice';
import { EditOutlined, PlusOutlined, InfoCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import { API_URL } from '../../utils/api';
import { exportToExcel } from '../../utils/exportUtils';

const ShopList = () => {
    const { shops, loading } = useSelector((state) => state.shops);
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [query, setQuery] = React.useState('');

    useEffect(() => {
        dispatch(fetchShops());
    }, [dispatch]);

    // Filter shops created by this BDE
    const bdeShops = shops.filter(s => s.bdeId === user?.id);

    // Further filter by search query
    const filteredShops = bdeShops.filter(s => {
        const q = query.toLowerCase();
        return (
            (s.name && s.name.toLowerCase().includes(q)) ||
            (s.ownerName && s.ownerName.toLowerCase().includes(q)) ||
            (s.pincode && s.pincode.includes(q))
        );
    });

    const screens = Grid.useBreakpoint();

    const handleExport = () => {
        if (!filteredShops || filteredShops.length === 0) {
            message.warning('No shops to export');
            return;
        }

        const dataToExport = filteredShops.map(s => ({
            ID: s.id,
            'Shop Name': s.name,
            'Owner Name': s.ownerName,
            Mobile: s.contactNumber,
            Pincode: s.pincode,
            Address: s.address,
            Status: s.status,
            'Rejection Reason': s.rejectionReason || ''
        }));

        exportToExcel(dataToExport, 'My_Shops');
        message.success('Shops exported successfully');
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'boardImageUrl',
            key: 'boardImageUrl',
            render: (text) => {
                if (!text) return 'No Image';
                // Fix encoding issue if present
                const src = text.startsWith('http') ? text : `${API_URL}${text}`;
                return <img src={src} alt="shop board" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />;
            },
        },
        {
            title: 'Shop Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Owner Name',
            dataIndex: 'ownerName',
            key: 'ownerName',
        },
        {
            title: 'Type',
            dataIndex: 'shopType',
            key: 'shopType',
            render: (text) => <Tag color="blue">{text || 'General Store'}</Tag>
        },
        {
            title: 'Pincode',
            dataIndex: 'pincode',
            key: 'pincode',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                let color = 'default';
                if (status === 'Verified') color = 'green';
                if (status === 'Pending') color = 'orange';
                if (status === 'Rejected') color = 'red';

                return (
                    <Space>
                        <Tag color={color}>{status || 'Verified'}</Tag>
                        {status === 'Rejected' && record.rejectionReason && (
                            <Tooltip title={`Rejection Reason: ${record.rejectionReason}`}>
                                <InfoCircleOutlined style={{ color: 'red' }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                // Allow edit only if not verified (assuming Verified shops are locked or need special permission?)
                // Actually, let's allow editing for Pending/Rejected to fix issues.
                const canEdit = record.status !== 'Verified';

                return (
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/bde/shops/edit/${record.id}`)}
                        disabled={!canEdit}
                    >
                        Edit
                    </Button>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
                <h2>My Shops</h2>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Input.Search
                        placeholder="Search my shops..."
                        style={{ width: 250, maxWidth: '100%' }}
                        onChange={e => setQuery(e.target.value)}
                    />
                    <Button icon={<FileExcelOutlined />} onClick={handleExport}>
                        Export to Excel
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/bde/shops/create')}>
                        Add Shop
                    </Button>
                </div>
            </div>

            <Card style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }} bodyStyle={{ padding: screens.md ? 24 : 12 }}>
                {!screens.md ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {filteredShops.map(shop => {
                            let statusColor = 'default';
                            if (shop.status === 'Verified') statusColor = 'green';
                            if (shop.status === 'Pending') statusColor = 'orange';
                            if (shop.status === 'Rejected') statusColor = 'red';

                            const imgSrc = shop.boardImageUrl ? (shop.boardImageUrl.startsWith('http') ? shop.boardImageUrl : `${API_URL}${shop.boardImageUrl}`) : null;
                            const canEdit = shop.status !== 'Verified';

                            return (
                                <Card key={shop.id} size="small"
                                    cover={imgSrc ? <img alt={shop.name} src={imgSrc} style={{ height: 150, objectFit: 'cover' }} /> : null}
                                    actions={[
                                        <Button
                                            type="link"
                                            icon={<EditOutlined />}
                                            onClick={() => navigate(`/bde/shops/edit/${shop.id}`)}
                                            disabled={!canEdit}
                                        >
                                            Edit
                                        </Button>
                                    ]}
                                >
                                    <Card.Meta
                                        title={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>{shop.name}</span>
                                                <Space size={4}>
                                                    <Tag color="blue">{shop.shopType || 'General Store'}</Tag>
                                                    <Tag color={statusColor}>{shop.status || 'Verified'}</Tag>
                                                </Space>
                                            </div>
                                        }
                                        description={
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Owner:</span>
                                                    <span>{shop.ownerName}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Pincode:</span>
                                                    <span>{shop.pincode}</span>
                                                </div>
                                                {shop.status === 'Rejected' && shop.rejectionReason && (
                                                    <div style={{ color: 'red', fontSize: 12 }}>Reason: {shop.rejectionReason}</div>
                                                )}
                                            </div>
                                        }
                                    />
                                </Card>
                            )
                        })}
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredShops}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </Card>
        </div>
    );
};

export default ShopList;
